import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * BusinessCard — editorial bento card.
 * Each card carries a 3px top-border in its sub-brand color.
 */
const BusinessCard = ({ business, span = 'md', index = 0 }) => {
  const isLarge = span === 'lg';
  const accent = `rgb(var(--sub-${business.subBrand}))`;

  return (
    <Link
      to={`/business/${business.id}`}
      data-testid={`business-card-${business.id}`}
      className={`group relative bg-white border border-brand-hairline hover:shadow-lift transition-shadow duration-300 flex flex-col focus-ring ${
        isLarge ? 'lg:col-span-8' : 'lg:col-span-4'
      }`}
    >
      {/* Top accent bar */}
      <div className="h-[3px] w-full" style={{ backgroundColor: accent }} aria-hidden />

      {/* Image */}
      <div className="relative overflow-hidden bg-brand-alabaster">
        <img
          src={business.heroImage}
          alt={`${business.name} — ${business.tagline}`}
          className={`w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ${
            isLarge ? 'h-72 md:h-96' : 'h-60 md:h-72'
          }`}
          loading={index === 0 ? 'eager' : 'lazy'}
        />
      </div>

      <div className="p-7 md:p-9 flex-1 flex flex-col">
        <div
          className="text-[11px] tracking-[0.16em] uppercase font-bold mb-4"
          style={{ color: accent }}
        >
          {business.tagline}
        </div>

        <h3 className={`font-display text-brand-oxford leading-[1.08] ${isLarge ? 'text-4xl md:text-5xl' : 'text-3xl md:text-[32px]'}`}>
          {business.name}
        </h3>

        <p className="mt-4 text-brand-slate leading-relaxed max-w-xl">
          {business.description}
        </p>

        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          {Object.entries(business.stats).slice(0, 2).map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-2">
              <span className="font-display text-2xl text-brand-oxford">{v}</span>
              <span className="text-[11px] tracking-[0.14em] uppercase text-brand-slate/80">
                {k.replace(/([A-Z])/g, ' $1').trim()}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-8">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 group-hover:gap-3 transition-all">
            Explore {business.name.split(' ')[0]}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default BusinessCard;
